import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  getSortedRowModel,
  type VisibilityState,
  type Row,
} from '@tanstack/react-table'
import { type ReactNode, useMemo, useState } from 'react'
import { SortingArrows } from '../../icons'
import { useMediaQuery } from '../../../../v3/hooks/useMediaQuery'
import { GenericTableMobileSortButton } from './GenericTableMobileSort'
import { GenericTableMobileFooter } from './GenericTableMobileFooter'
import { Loader } from '../Loader'
import Card from '../Card'

export interface ColumnMeta {
  alignmentType?: 'left' | 'right'
  mobileFooterLabel?: string
}

type ColumnWidthProps = {
  width?: number
  minWidth?: number
  maxWidth?: number
}

type GenericTableProps<T> = {
  className?: string
  isLoading?: boolean
  data: T[]
  mobileCardContent: (row: Row<T>, onRowClick?: (row: Row<T>) => void) => ReactNode
  columns: ColumnDef<T, any>[]
  columnVisibility?: VisibilityState
  columnWidths?: Record<string, ColumnWidthProps>
  defaultColumnWidth?: ColumnWidthProps
  onColumnVisibilityChange?: (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void
  onRowClick?: (row: Row<T>) => void
  tableMeta?: ColumnMeta
}

const GenericTable = <T,>({
  data,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  tableMeta,
  isLoading,
  columnWidths,
  defaultColumnWidth,
  mobileCardContent,
  onRowClick,
  className,
}: GenericTableProps<T>) => {
  const [sorting, setSorting] = useState([])

  const columnsWidth = useMemo(() => {
    const widths = columnWidths ?? {}

    return columns.map((col: any) => {
      const id = col.id ?? col.accessorKey
      if (!id) return col

      const width = widths[String(id)]
      if (!width) return col

      return {
        ...col,
        size: width.width ?? col.width,
        minSize: width.minWidth ?? col.minWidth,
        maxSize: width.maxWidth ?? col.maxWidth,
      }
    })
  }, [columns, columnWidths])

  const table = useReactTable({
    data,
    columns: columnsWidth,
    defaultColumn: {
      size: defaultColumnWidth?.width ?? 150,
      minSize: defaultColumnWidth?.width ?? 80,
      maxSize: defaultColumnWidth?.width ?? 300,
    },
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetPageIndex: false,
    meta: tableMeta,
  })

  const isDesktop = useMediaQuery('(min-width: 768px)')
  const hasData = table.getRowCount() !== 0

  return (
    <Card
      className={`${className} ${isDesktop ? 'bg-white px-4' : 'border-none overflow-visible !max-h-max !shadow-none'} w-full animate-popUpFromBottom`}
    >
      {!isDesktop && hasData && <GenericTableMobileSortButton<T> table={table} />}
      {hasData && (
        <div className="md:hidden">
          <GenericTableMobileFooter<T> table={table} />
        </div>
      )}
      <table className="w-full table-fixed py-2">
        <thead className="hidden md:table-header-group bg-white sticky top-0 z-[2] h-12">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="pt-4 px-4">
              {group.headers.map((header) => {
                const meta = header.column.columnDef.meta as ColumnMeta | undefined
                const alignment = meta?.alignmentType === 'right' ? 'justify-end' : 'justify-start'

                return (
                  <th
                    key={header.id}
                    style={{ width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined }}
                    className={`${alignment} text-primaryText text-sm px-4 py-2 font-bold text-nowrap overflow-hidden text-ellipsis whitespace-nowrap ${
                      header.column.getCanSort() ? 'cursor-pointer' : ''
                    }`}
                    onClick={header.column.getToggleSortingHandler?.()}
                  >
                    {header.isPlaceholder ? null : (
                      <div className={`flex items-center ${alignment} gap-1`}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <div className="scale-75">
                          {header.column.getCanSort() &&
                            {
                              asc: <SortingArrows sorting="asc" />,
                              desc: <SortingArrows sorting="desc" />,
                              false: <SortingArrows sorting="none" />,
                            }[String(header.column.getIsSorted()) as 'asc' | 'desc' | 'false']}
                        </div>
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowCount() === 0 && (
            <tr>
              <td colSpan={table.getVisibleFlatColumns().length}>
                <div className="py-8 flex flex-col items-center justify-center gap-2">
                  {isLoading && (
                    <>
                      <Loader />
                      <p>Henter data...</p>
                    </>
                  )}
                  {!isLoading && <div> Ingen data tilgjengelig...</div>}
                </div>
              </td>
            </tr>
          )}
          {table.getRowModel().rows.map((row, index) => {
            return (
              <TableRow<T>
                key={row.id}
                row={row}
                rowIndex={index}
                mobileCardContent={mobileCardContent}
                onRowClick={onRowClick}
                isDesktop={isDesktop}
              />
            )
          })}
        </tbody>

        {table.getFooterGroups()?.length > 0 && (
          <tfoot className="hidden md:table-footer-group sticky bottom-0 bg-white">
            {table.getFooterGroups().map((footerGroup) => (
              <tr key={footerGroup.id} className="h-12 font-bold text-sm">
                {footerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as ColumnMeta
                  const alignment = meta?.alignmentType === 'right' ? 'text-right' : 'text-left'

                  return (
                    <td key={header.id} className={`px-4 ${alignment}`}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </Card>
  )
}

export default GenericTable

function TableRow<T>({
  row,
  rowIndex,
  mobileCardContent,
  onRowClick,
  isDesktop,
}: {
  row: Row<T>
  rowIndex: number
  mobileCardContent: (row: Row<T>) => ReactNode
  onRowClick?: (row: Row<T>) => void
  isDesktop: boolean
}) {
  if (!isDesktop) {
    return (
      <tr key={rowIndex}>
        <td colSpan={row.getVisibleCells().length}>{<TableCard row={row} content={mobileCardContent} />}</td>
      </tr>
    )
  }

  return (
    <tr
      key={rowIndex}
      className={`${onRowClick && 'cursor-pointer'} h-12 font-normal text-sm text-primaryText odd:bg-grayLight hover:font-bold hover:bg-blueLight group`}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      {row.getVisibleCells().map((cell, index) => {
        const meta = cell.column.columnDef.meta as ColumnMeta
        const alignment = meta?.alignmentType === 'right' ? 'text-right' : 'text-left'

        return (
          <td
            key={`${index}_${cell.id}`}
            style={{ width: `${cell.column.getSize()}px` }}
            className={`px-4 overflow-hidden text-ellipsis whitespace-nowrap ${alignment}`}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        )
      })}
    </tr>
  )
}

function TableCard<T>({
  row,
  content,
  onRowClick,
}: {
  row: Row<T>
  content: (row: Row<T>, onRowClick?: (row: Row<T>) => void) => ReactNode
  onRowClick?: (row: Row<T>) => void
}) {
  return <Card className="w-full mb-4 bg-white">{content(row, onRowClick)}</Card>
}
